import { useMemo } from 'react';
import { Activity, CalendarClock, CirclePlay, Database, MonitorCheck, MonitorOff, MonitorPlay, Plus, UploadCloud } from 'lucide-react';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import StatCard from '../../components/StatCard';
import Card from '../../components/Card';
import Badge from '../../components/Badge';
import { useAuth } from '../../context/AuthContext';
import { useTenantCollection } from '../../hooks/useTenantCollection';
import { formatDateTime, onlineFromLastSeen, screenLimitLabel } from '../../utils/format';

export default function DashboardPage() {
  const { profile, tenant, subscription } = useAuth();
  const tenantId = profile?.tenantId;
  const { items: screens } = useTenantCollection('screens', tenantId);
  const { items: media } = useTenantCollection('media', tenantId);
  const { items: playlists } = useTenantCollection('playlists', tenantId);
  const { items: schedules } = useTenantCollection('schedules', tenantId);
  const online = useMemo(() => screens.filter(item => onlineFromLastSeen(item.lastSeen)).length, [screens]);
  const activeSchedules = schedules.filter(item => item.status === 'active').length;
  const usagePct = subscription?.maxScreens >= 999 ? Math.min(100, screens.length) : Math.min(100, ((screens.length || 0) / Math.max(1, subscription?.maxScreens || 1)) * 100);

  const recentScreens = [...screens].sort((a, b) => (b.lastSeen?.seconds || 0) - (a.lastSeen?.seconds || 0)).slice(0, 5);

  return (
    <>
      <PageHeader eyebrow="Workspace overview" title={`Good day, ${profile?.name?.split(' ')[0] || 'Admin'}`} description={`Monitor ${tenant?.companyName || 'your workspace'} screens, content and campaigns from one place.`}
        actions={<><Link to="/tenant/screens" className="btn btn-soft"><Plus size={17} /> Add screen</Link><Link to="/tenant/media" className="btn btn-primary"><UploadCloud size={17} /> Upload media</Link></>} />

      <div className="stats-grid four">
        <StatCard icon={MonitorPlay} label="Screens" value={screens.length} detail={`${online} online`} tone="blue" />
        <StatCard icon={MonitorCheck} label="Online now" value={online} detail={`${Math.max(0, screens.length - online)} offline`} tone="green" />
        <StatCard icon={Database} label="Media assets" value={media.length} detail={`${playlists.length} playlists`} tone="purple" />
        <StatCard icon={CalendarClock} label="Active schedules" value={activeSchedules} detail={`${schedules.length} total`} tone="amber" />
      </div>

      <div className="dashboard-grid">
        <Card className="hero-card">
          <div className="card-head"><div><span className="eyebrow">Screen entitlement</span><h3>{subscription?.planName || 'No active plan'}</h3></div><Badge tone={subscription?.isActive ? 'success' : subscription?.status === 'expired' ? 'danger' : 'warning'}>{subscription?.status || 'inactive'}</Badge></div>
          <div className="hero-metric"><strong>{screens.length}</strong><span>of {screenLimitLabel(subscription?.maxScreens || 0)} screens used</span></div>
          <div className="big-meter"><span style={{ width: `${usagePct}%` }} /></div>
          <p>{subscription?.message || 'Choose a plan to begin pairing screens.'}</p>
          <Link className="text-link" to="/tenant/subscription">Manage subscription →</Link>
        </Card>

        <Card>
          <div className="card-head"><div><span className="eyebrow">Content engine</span><h3>Publishing readiness</h3></div><CirclePlay size={20} /></div>
          <div className="readiness-list"><div><span>Media library</span><strong>{media.length ? 'Ready' : 'Add media'}</strong></div><div><span>Playlists</span><strong>{playlists.length ? `${playlists.length} built` : 'Create one'}</strong></div><div><span>Schedules</span><strong>{activeSchedules ? `${activeSchedules} live` : 'Not scheduled'}</strong></div></div>
          <Link className="btn btn-soft btn-wide" to="/tenant/playlists">Open content workflow</Link>
        </Card>
      </div>

      <div className="content-grid two-one">
        <Card>
          <div className="card-head"><div><span className="eyebrow">Device pulse</span><h3>Recently active screens</h3></div><Activity size={19} /></div>
          <div className="list-stack">
            {recentScreens.length ? recentScreens.map(screen => {
              const isOnline = onlineFromLastSeen(screen.lastSeen);
              return <div className="list-row" key={screen.id}><div className={`device-dot ${isOnline ? 'online' : 'offline'}`}>{isOnline ? <MonitorCheck size={18} /> : <MonitorOff size={18} />}</div><div className="grow"><strong>{screen.screenName || 'Unnamed screen'}</strong><small>{screen.deviceModel || 'Waiting for TV pairing'}</small></div><div className="align-right"><Badge tone={screen.status === 'pending' ? 'warning' : isOnline ? 'success' : 'neutral'}>{screen.status === 'pending' ? 'Pending' : isOnline ? 'Online' : 'Offline'}</Badge><small>{formatDateTime(screen.lastSeen, 'Never')}</small></div></div>;
            }) : <div className="mini-empty">No screens yet. Add your first screen to start.</div>}
          </div>
        </Card>

        <Card className="quick-card"><span className="eyebrow">Quick actions</span><h3>Keep content moving</h3><Link to="/tenant/screens"><MonitorPlay />Manage screens</Link><Link to="/tenant/media"><UploadCloud />Upload content</Link><Link to="/tenant/scheduler"><CalendarClock />Schedule campaign</Link></Card>
      </div>
    </>
  );
}
